import { createAsyncThunk, createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import axios from "axios";
import type { WritableDraft } from "immer";
import { enqueueSnackbar } from "notistack";
import PermissionTemplatesAPIService from "permissions/_api/permission-templates-api-service";
import PermissionTemplateQuerySchema, {
  PermissionTemplateQueryJSON,
} from "permissions/_api/queries/permission-template-query-schema";
import PermissionTemplateQuery from "permissions/_api/request-contracts/permission-template-query";
import { PermissionTemplateJSON } from "permissions/templates/_schemas/permission-template-schema";
import PermissionTemplate from "permissions/templates/permission-template";
import Session from "users/__session/session";

type PermissionTemplateStoreState = {
  byId: {
    entries: Record<string, PermissionTemplateJSON>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  byQuery: {
    entries: Record<string, PaginatedResponse<PermissionTemplateJSON>>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
  };
  interfaceQueries: {
    entries: Record<string, PermissionTemplateQueryJSON>;
  };
};

const initialState: PermissionTemplateStoreState = {
  byId: {
    entries: {},
    loading: {},
    error: {},
  },
  byQuery: {
    entries: {},
    loading: {},
    error: {},
  },
  interfaceQueries: {
    entries: {},
  },
};

export const populatePermissionTemplatesByQuery = createAsyncThunk(
  "permissionTemplates/getPermissionTemplatesByQuery",
  async ({ session, query }: { session: Session; query: PermissionTemplateQuery }, thunkAPI) => {
    const apiService = new PermissionTemplatesAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);

    try {
      return await apiService.getPermissionTemplates(query, abortController);
    } catch (error) {
      if (axios.isCancel(error)) {
        return thunkAPI.rejectWithValue("Request canceled");
      }
      console.error(error);
      if (axios.isAxiosError(error)) {
        return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
      }
      return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  }
);

export const addPermissionTemplate = createAsyncThunk<
  PermissionTemplateJSON,
  { session: Session; template: PermissionTemplate },
  { rejectValue: string }
>("permissionTemplates/addPermissionTemplate", async ({ session, template }, thunkAPI) => {
  try {
    const api = new PermissionTemplatesAPIService(session);
    return await api.addPermissionTemplate(template);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
    }
    return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
  }
});

export const updatePermissionTemplate = createAsyncThunk<
  PermissionTemplateJSON,
  { session: Session; template: PermissionTemplate },
  { rejectValue: string }
>("permissionTemplates/updatePermissionTemplate", async ({ session, template }, thunkAPI) => {
  try {
    const api = new PermissionTemplatesAPIService(session);
    return await api.updatePermissionTemplate(template);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
    }
    return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
  }
});

export const removePermissionTemplate = createAsyncThunk<
  void,
  { session: Session; templateId: Guid },
  { rejectValue: string }
>("permissionTemplates/removePermissionTemplate", async ({ session, templateId }, thunkAPI) => {
  try {
    const api = new PermissionTemplatesAPIService(session);
    await api.deletePermissionTemplateById(templateId.value);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return thunkAPI.rejectWithValue(String(error?.response?.data ?? error));
    }
    return thunkAPI.rejectWithValue(error?.toString() ?? "An error occurred.");
  }
});

const resetByQueryReducer = (state: WritableDraft<PermissionTemplateStoreState>) => {
  state.byQuery = initialState.byQuery;
};

const permissionTemplatesSlice = createSlice({
  name: "permissionTemplates",
  initialState,
  reducers: {
    updatePermissionTemplateInterfaceQuery: (
      state,
      action: PayloadAction<{
        interfaceName: string;
        query: PermissionTemplateQuery;
      }>
    ) => {
      const { interfaceName, query } = action.payload;

      state.interfaceQueries.entries[interfaceName] = PermissionTemplateQuerySchema.serialize(query);
    },
    removeTemplate: (state, action: PayloadAction<PermissionTemplate | Guid>) => {
      let templateId: Guid | undefined;
      if (action.payload instanceof Guid) {
        templateId = action.payload;
      } else {
        templateId = action.payload.id;
      }
      if (!templateId) {
        throw new Error("Template id not found");
      }
      delete state.byId.entries[templateId.value];
      state.byQuery = {
        entries: {},
        loading: {},
        error: {},
      };
    },
    resetByQuery: resetByQueryReducer,
  },
  extraReducers: (builder) => {
    builder.addCase(populatePermissionTemplatesByQuery.pending, (state, action) => {
      if (!action.meta.arg.query?.filterParameters) {
        return;
      }
      const queryKey = action.meta.arg.query.cacheKey;
      state.byQuery.loading[queryKey] = true;
      state.byQuery.error[queryKey] = null;
    });
    builder.addCase(populatePermissionTemplatesByQuery.fulfilled, (state, action) => {
      if (!action.meta.arg.query?.filterParameters) {
        return;
      }
      const queryKey = action.meta.arg.query.cacheKey;
      state.byQuery.loading[queryKey] = false;
      state.byQuery.entries[queryKey] = action.payload;
    });
    builder.addCase(populatePermissionTemplatesByQuery.rejected, (state, action) => {
      if (!action.meta.arg.query?.filterParameters) {
        return;
      }      
      const queryKey = action.meta.arg.query.cacheKey;
      state.byQuery.loading[queryKey] = false;
      state.byQuery.error[queryKey] = action.payload as string;
    });

    // add.pending - optimistic set
    builder.addCase(addPermissionTemplate.pending, (state, action) => {
      const id = action.meta.arg.template.id;
      if (id) {
        state.byId.entries[id.value] = action.meta.arg.template.toJSON();
        state.byId.loading[id.value] = true;
        state.byId.error[id.value] = null;
      }
      // else: no id yet, skip replace; rely on fulfilled below to insert
    });
    // add.fulfilled - server truth
    builder.addCase(addPermissionTemplate.fulfilled, (state, action) => {
      const updated = action.payload;
      const id = updated?.id ?? action.meta.arg.template.id;
      if (!id) return;

      state.byId.entries[id] = updated;
      state.byId.loading[id] = false;
      state.byId.error[id] = null;
      
      resetByQueryReducer(state);
    });
    // add.rejected – rollback
    builder.addCase(addPermissionTemplate.rejected, (state, action) => {
      const orig = action.meta.arg.template;
      if (!orig?.id) return;

      state.byId.entries[orig.id.value] = orig.toJSON();
      state.byId.loading[orig.id.value] = false;
      state.byId.error[orig.id.value] = action.payload as string;
    });

    // update.pending - optimistic set
    builder.addCase(updatePermissionTemplate.pending, (state, action) => {
      const id = action.meta.arg.template.id;
      if (!id) return;

      state.byId.entries[id.value] = action.meta.arg.template.toJSON();
      state.byId.loading[id.value] = true;
      state.byId.error[id.value] = null;
    });
    // update.fulfilled - server truth
    builder.addCase(updatePermissionTemplate.fulfilled, (state, action) => {
      const updated = action.payload;
      const id = updated?.id ?? action.meta.arg.template.id;
      if (!id) return;

      state.byId.entries[id] = updated;
      state.byId.loading[id] = false;
      state.byId.error[id] = null;

      resetByQueryReducer(state);
    });
    // update.rejected - rollback
    builder.addCase(updatePermissionTemplate.rejected, (state, action) => {
      const orig = action.meta.arg.template;
      if (!orig?.id) return;
      
      state.byId.entries[orig.id.value] = orig.toJSON();
      state.byId.loading[orig.id.value] = false;
      state.byId.error[orig.id.value] = action.payload as string;
    });

    // remove.pending - optimistic remove
    builder.addCase(removePermissionTemplate.pending, (state, action) => {
      const id = action.meta.arg.templateId;
      state.byId.loading[id.value] = true;
      state.byId.error[id.value] = null;

      delete state.byId.entries[id.value];
    });
    // remove.fulfilled - nothing to do
    builder.addCase(removePermissionTemplate.fulfilled, (state, action) => {
      const id = action.meta.arg.templateId;
      state.byId.loading[id.value] = false;
      state.byId.error[id.value] = null;

      resetByQueryReducer(state);
    });
    // remove.rejected - rollback
    builder.addCase(removePermissionTemplate.rejected, (state, action) => {
      const id = action.meta.arg.templateId;
      state.byId.loading[id.value] = false;
      state.byId.error[id.value] = action.payload as string;
      enqueueSnackbar("Error deleting template: " + action.payload, { variant: "error" });
    });
  },
});

export const { updatePermissionTemplateInterfaceQuery, removeTemplate } = permissionTemplatesSlice.actions;

const selectPermissionTemplatesByQuery = 
  createSelector(
    [
      (state: RootState) => state.permissionTemplates.byQuery.entries,
      (_: RootState, query?: PermissionTemplateQuery) => query?.cacheKey
    ],
    (entries, queryKey) => {
      if (!queryKey) return;
      const pagedJson = entries[queryKey];
      if (!pagedJson) return;
      const templateData = PermissionTemplate.fromJSONArray(pagedJson.data);
      return PaginatedResponse.from<PermissionTemplate>(templateData, pagedJson?.headers);
    }
  );
export const getPermissionTemplatesByQuery = (query?: PermissionTemplateQuery) => (state: RootState) => 
  selectPermissionTemplatesByQuery(state, query);

export const getIsLoadingPermissionTemplatesByQuery = (query?: PermissionTemplateQuery) => (state: RootState) => {
  if (!query) return false;
  return state.permissionTemplates.byQuery.loading[query.cacheKey] ?? false;
};
export const getErrorLoadingPermissionTemplatesByQuery = (query?: PermissionTemplateQuery) => (state: RootState) => {
  if (!query) return false;
  return state.permissionTemplates.byQuery.error[query.cacheKey] ?? false;
};

const selectPermissionTemplateById = 
  createSelector(
    [
      (state: RootState) => state.permissionTemplates.byId.entries,
      (_: RootState, id: Guid) => id.value
    ],
    (entries, id) => {
      const template = entries[id];
      if (!template) return;
      return PermissionTemplate.fromJSON(template);
    }
  );
export const getPermissionTemplateById = (id: Guid) => (state: RootState) => 
  selectPermissionTemplateById(state, id);

export const getIsLoadingPermissionTemplateById = (id: Guid) => (state: RootState) =>
  state.permissionTemplates.byId.loading[id.value];
export const getErrorLoadingPermissionTemplateById = (id: Guid) => (state: RootState) =>
  state.permissionTemplates.byId.error[id.value];

const selectPermissionTemplatesInterfaceQueryByInterface = createSelector(
  [
    (state: RootState) => state.permissionTemplates.interfaceQueries.entries,
    (_: RootState, interfaceName: string) => interfaceName,
  ],
  (entries, interfaceName) => {
    const entry = entries[interfaceName];
    if (!entry) return;
    return PermissionTemplateQuerySchema.deserialize(entry);
  }
);
export const getPermissionTemplatesQueryByInterface = (interfaceName: string) => (state: RootState) =>
  selectPermissionTemplatesInterfaceQueryByInterface(state, interfaceName);

export const permissionTemplateIsLoading = (id: Guid) => (state: RootState) =>
  state.permissionTemplates.byId.loading[id.value];

export default permissionTemplatesSlice;
